import { RECURRENCE_LABELS, RECURRENCE_UNITS } from '@/consts/recurrence';
import { translateCheckoutBuilder } from '@/utils/checkoutBuilderI18n';
import { DEFAULT_PRODUCT_CURRENCY_CODE, formatMoney } from '@/utils/currency';

const RECURRENCE_UNIT_KEYS = {
  [RECURRENCE_UNITS[1]]: 'day',
  [RECURRENCE_UNITS[7]]: 'week',
  [RECURRENCE_UNITS[30]]: 'month',
  [RECURRENCE_UNITS[60]]: 'bimonth',
  [RECURRENCE_UNITS[90]]: 'quarter',
  [RECURRENCE_UNITS[180]]: 'semester',
  [RECURRENCE_UNITS[365]]: 'year',
};

const RECURRENCE_LABEL_KEYS = {
  [RECURRENCE_LABELS[1]]: 'daily',
  [RECURRENCE_LABELS[7]]: 'weekly',
  [RECURRENCE_LABELS[30]]: 'monthly',
  [RECURRENCE_LABELS[60]]: 'bimonthly',
  [RECURRENCE_LABELS[90]]: 'quarterly',
  [RECURRENCE_LABELS[180]]: 'semiannual',
  [RECURRENCE_LABELS[365]]: 'annual',
};

export function fSubscriptionPrice(price, recurrencePeriod, currency = 'BRL') {
  const periodKey = RECURRENCE_UNIT_KEYS[RECURRENCE_UNITS[recurrencePeriod]] ?? 'month';
  const period = translateCheckoutBuilder(currency, `recurrence.units.${periodKey}`, {
    defaultValue: periodKey,
  });

  return `${formatMoney(Number(price), currency)} / ${period}`;
}

export function fSubscriptionRenewalLabel(recurrencePeriod, currency = 'BRL') {
  const periodKey = RECURRENCE_LABEL_KEYS[RECURRENCE_LABELS[recurrencePeriod]] ?? 'monthly';
  const period = translateCheckoutBuilder(currency, `recurrence.labels.${periodKey}`, {
    defaultValue: periodKey,
  });

  return translateCheckoutBuilder(currency, 'recurrence.renewal', { period });
}

const normalizeCurrencyCode = (value) => {
  const code =
    value != null && String(value).trim() !== '' ? String(value).trim().toUpperCase() : '';
  return /^[A-Z]{3}$/.test(code) ? code : '';
};

const normalizeMetricAmount = (value) => Number(value) || 0;

const normalizeMetricEntry = (currency, value, fallbackCurrency) => ({
  currency:
    normalizeCurrencyCode(currency) ||
    normalizeCurrencyCode(value?.currency) ||
    normalizeCurrencyCode(value?.code) ||
    fallbackCurrency,
  value: normalizeMetricAmount(
    typeof value === 'object' ? value?.value ?? value?.amount ?? value?.total : value
  ),
});

export function normalizeCurrencyMetricBreakdown(
  metricValue,
  fallbackCurrency = DEFAULT_PRODUCT_CURRENCY_CODE
) {
  const fallback = normalizeCurrencyCode(fallbackCurrency) || DEFAULT_PRODUCT_CURRENCY_CODE;

  if (Array.isArray(metricValue)) {
    return metricValue.map((item) =>
      normalizeMetricEntry(item?.currency ?? item?.code, item, fallback)
    );
  }

  if (metricValue && typeof metricValue === 'object') {
    if (metricValue.currency || metricValue.code) {
      return [normalizeMetricEntry(metricValue.currency ?? metricValue.code, metricValue, fallback)];
    }

    return Object.entries(metricValue).map(([currency, value]) =>
      normalizeMetricEntry(currency, value, fallback)
    );
  }

  return [normalizeMetricEntry(fallback, metricValue, fallback)];
}

export function getSubscriptionMetricBreakdown(
  subscriptions,
  metricKey,
  fallbackCurrency = DEFAULT_PRODUCT_CURRENCY_CODE
) {
  const directBreakdownKeys = [
    `${metricKey}_by_currency`,
    `${metricKey}ByCurrency`,
    `${metricKey}_breakdown`,
    `${metricKey}Breakdown`,
  ];

  const directBreakdownKey = directBreakdownKeys.find((key) => subscriptions?.[key] != null);

  if (directBreakdownKey) {
    return normalizeCurrencyMetricBreakdown(subscriptions[directBreakdownKey], fallbackCurrency);
  }

  const groupedMetrics =
    subscriptions?.metrics_by_currency ??
    subscriptions?.metricsByCurrency ??
    subscriptions?.currency_metrics ??
    subscriptions?.currencyMetrics;

  if (Array.isArray(groupedMetrics)) {
    const values = groupedMetrics
      .map((item) => ({
        currency: item?.currency ?? item?.code,
        value: item?.[metricKey],
      }))
      .filter((item) => item.value != null);

    if (values.length > 0) {
      return normalizeCurrencyMetricBreakdown(values, fallbackCurrency);
    }
  }

  if (groupedMetrics && typeof groupedMetrics === 'object') {
    const values = Object.entries(groupedMetrics).reduce((acc, [currency, item]) => {
      const value = typeof item === 'object' ? item?.[metricKey] : undefined;
      if (value != null) acc[currency] = value;
      return acc;
    }, {});

    if (Object.keys(values).length > 0) {
      return normalizeCurrencyMetricBreakdown(values, fallbackCurrency);
    }
  }

  return normalizeCurrencyMetricBreakdown(subscriptions?.[metricKey], fallbackCurrency);
}

export function getSubscriptionCurrency(
  subscription,
  fallbackCurrency = DEFAULT_PRODUCT_CURRENCY_CODE
) {
  const fallback = normalizeCurrencyCode(fallbackCurrency) || DEFAULT_PRODUCT_CURRENCY_CODE;

  return (
    normalizeCurrencyCode(subscription?.currency) ||
    normalizeCurrencyCode(subscription?.currency_code) ||
    normalizeCurrencyCode(subscription?.currencyCode) ||
    normalizeCurrencyCode(subscription?.product?.currency) ||
    normalizeCurrencyCode(subscription?.offer?.currency) ||
    normalizeCurrencyCode(subscription?.parent_order?.currency) ||
    fallback
  );
}
